import { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const PLATFORM_COLORS = {
  '스마트스토어': { bg: '#E1F5EE', color: '#0F6E56' },
  '셀러허브': { bg: '#FBEAF0', color: '#72243E' },
  '토스쇼핑': { bg: '#E0F4F8', color: '#0B6475' },
  '쿠팡': { bg: '#E6F1FB', color: '#185FA5' },
  '11번가': { bg: '#EEEDFE', color: '#534AB7' },
  '지마켓': { bg: '#FAEEDA', color: '#854F0B' },
  '옥션': { bg: '#FCEBEB', color: '#A32D2D' },
};

function PlatformBadge({ platform }) {
  const style = PLATFORM_COLORS[platform] || { bg: '#f0f0f0', color: '#666' };
  return (
    <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '4px', fontWeight: '500', background: style.bg, color: style.color, whiteSpace: 'nowrap' }}>
      {platform}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    claim: { label: '반품·교환 진행중', bg: '#FAEEDA', color: '#854F0B' },
    cancel: { label: '주문취소', bg: '#FCEBEB', color: '#A32D2D' },
    buy: { label: '구매완료', bg: '#F1EFE8', color: '#5F5E5A' },
    ship: { label: '배송중', bg: '#E6F1FB', color: '#185FA5' },
    done: { label: '배송완료', bg: '#E1F5EE', color: '#0F6E56' },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '4px', fontWeight: '500', background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

export default function Sales() {
  const [period, setPeriod] = useState('today');
  const [platformFilter, setPlatformFilter] = useState('');
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ order_count: 0, total_sales: 0, total_settlement: 0, total_purchase: 0, total_margin: 0 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(20);
  const latestReqId = useRef(0);
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const isMonthView = period === 'month' || period === 'lastmonth';

  const getDateRange = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fmt = d => {
      const y = d.getFullYear();
      const m = String(d.getMonth()+1).padStart(2,'0');
      const day = String(d.getDate()).padStart(2,'0');
      return `${y}-${m}-${day}`;
    };
    if (period === 'today') return { dateFrom: fmt(today), dateTo: fmt(new Date(today.getTime() + 86400000)) };
    if (period === 'yesterday') {
      const y = new Date(today.getTime() - 86400000);
      return { dateFrom: fmt(y), dateTo: fmt(today) };
    }
    if (period === 'month') return { dateFrom: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), dateTo: fmt(new Date(today.getTime() + 86400000)) };
    if (period === 'lastmonth') {
      const f = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const t = new Date(today.getFullYear(), today.getMonth(), 1);
      return { dateFrom: fmt(f), dateTo: fmt(t) };
    }
    if (period === 'custom') {
      if (!customFrom || !customTo) return {};
      const fmt2 = d => {
        const y = d.getFullYear();
        const m = String(d.getMonth()+1).padStart(2,'0');
        const day = String(d.getDate()).padStart(2,'0');
        return `${y}-${m}-${day}`;
      };
      const toNext = new Date(customTo);
      toNext.setDate(toNext.getDate() + 1);
      return { dateFrom: fmt2(customFrom), dateTo: fmt2(toNext) };
    }
    return {};
  }, [period, customFrom, customTo]);

  const fetchData = useCallback(async () => {
    const reqId = ++latestReqId.current;
    const { dateFrom, dateTo } = getDateRange();
    const baseParams = {};
    if (platformFilter) baseParams.platform = platformFilter;
    if (dateFrom) baseParams.dateFrom = dateFrom;
    if (dateTo) baseParams.dateTo = dateTo;
    try {
      // summary 먼저 조회
      const summaryRes = await api.get('/sales/summary', { params: baseParams });
      if (reqId !== latestReqId.current) return;
      const summaryData = summaryRes.data;
      setSummary(summaryData);
      // 이번달/지난달: summary 건수로 전체 조회 (페이지네이션 없음)
      const orderCount = parseInt(summaryData.order_count || 0);
      const limit = isMonthView ? Math.max(orderCount, 1) : pageLimit;
      const ordersRes = await api.get('/sales/orders', {
        params: { ...baseParams, page: isMonthView ? 1 : currentPage, limit }
      });
      if (reqId !== latestReqId.current) return;
      setOrders(ordersRes.data.orders);
      setTotal(ordersRes.data.total);
      setTotalPages(ordersRes.data.totalPages);
    } catch (err) { console.error(err); }
  }, [currentPage, pageLimit, platformFilter, getDateRange, isMonthView]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setCurrentPage(1); }, [period, platformFilter, customFrom, customTo]);

  const fmt = n => parseInt(n || 0).toLocaleString();
  const marginRateColor = (rate) => {
    const r = parseFloat(rate);
    if (isNaN(r)) return '#aaa';
    if (r >= 15) return '#185FA5';
    if (r >= 10) return '#1D9E75';
    return '#EF9F27';
  };

  // 이번달/지난달 → 일별 그룹핑
  const groupByDay = () => {
    const map = {};
    orders.forEach(o => {
      const day = o.ordered_at
        ? new Date(o.ordered_at).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\.\s*/g, '-').replace(/-$/, '')
        : '미확인';
      if (!map[day]) map[day] = [];
      map[day].push(o);
    });
    return map;
  };

  const thStyle = { padding: '7px 8px', color: '#888', fontWeight: '400', borderBottom: '0.5px solid #e8e8e8', fontSize: '11px', background: '#f9f9f9', textAlign: 'center', whiteSpace: 'nowrap', lineHeight: '1.4' };
  const tdStyle = { padding: '7px 8px', borderBottom: '0.5px solid #e8e8e8', textAlign: 'center', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  const summaryTitle = () => {
    const now = new Date();
    if (period === 'today') return `${now.getMonth()+1}월 ${now.getDate()}일 종합 · ${summary.order_count}건`;
    if (period === 'yesterday') { const y = new Date(now.getTime()-86400000); return `${y.getMonth()+1}월 ${y.getDate()}일 종합 · ${summary.order_count}건`; }
    if (period === 'month') return `${now.getMonth()+1}월 종합 · ${summary.order_count}건`;
    if (period === 'lastmonth') return `${now.getMonth()}월 종합 · ${summary.order_count}건`;
    if (period === 'custom') {
      if (!customFrom || !customTo) return `기간 선택 · ${summary.order_count}건`;
      const fmt2 = d => `${d.getMonth()+1}/${d.getDate()}`;
      return `${fmt2(customFrom)} ~ ${fmt2(customTo)} 종합 · ${summary.order_count}건`;
    }
    return `종합 · ${summary.order_count}건`;
  };

  const renderOrderRow = (order, idx) => {
    const items = order.items || [];
    const isCancelOrRefund = order.status === 'cancel' || order.status === 'refund_done';
    const salePriceTotal = items.reduce((s, i) => s + (i.sale_price || 0), 0) + (isCancelOrRefund ? 0 : (order.shipping_fee || 0));
    const settlement = isCancelOrRefund ? 0 : items.reduce((s, i) => s + (i.settlement_price || 0), 0) + (order.shipping_settlement || 0);
    const purchase = isCancelOrRefund ? 0 : items.reduce((s, i) => { const pr = i.purchase_record || {}; return s + (pr.purchase_price || 0) + (pr.shipping_cost || 0); }, 0);
    const extraCost = items.reduce((s, i) => s + (i.purchase_record?.extra_cost || 0), 0);
    const pr = items[0]?.purchase_record || {};
    const vat = isCancelOrRefund ? 0 : Math.round(salePriceTotal * 0.1 - purchase * 0.1);
    const margin = isCancelOrRefund ? extraCost : settlement - purchase - vat - extraCost;
    const isNegative = order.status === 'cancel' || order.status === 'claim' || order.status === 'refund_done';

    return (
      <tr key={order.id} style={{ background: isNegative ? '#fff8f8' : 'white' }}
        onMouseEnter={e => e.currentTarget.style.background = isNegative ? '#ffefef' : '#f9f9f9'}
        onMouseLeave={e => e.currentTarget.style.background = isNegative ? '#fff8f8' : 'white'}>
        <td style={tdStyle}>{idx}</td>
        <td style={{ ...tdStyle, fontSize: '10px', color: '#999' }}>{order.ordered_at ? new Date(order.ordered_at).toLocaleString('ko-KR', {timeZone:'Asia/Seoul', year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit'}) : '-'}</td>
        <td style={{ ...tdStyle, fontSize: '10px', color: '#aaa' }}>
          <div>{order.order_number}</div>
          {order.items && order.items.length > 1 && (
            <div style={{ fontSize: '10px', color: '#185FA5', background: '#E6F1FB', borderRadius: '4px', padding: '1px 5px', marginTop: '2px', display: 'inline-block' }}>
              상품 {order.items.length}건
            </div>
          )}
        </td>
        <td style={tdStyle}><PlatformBadge platform={order.platform} /></td>
        <td style={{ ...tdStyle, textAlign: 'left', maxWidth: '180px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            {(order.status === 'claim' || (order.status === 'cancel' && (pr.extra_cost || 0) !== 0)) && <StatusBadge status={order.status} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{items[0]?.product_name || '-'}</span>
          </div>
        </td>
        <td style={{ ...tdStyle, color: isCancelOrRefund ? '#aaa' : '#1a1a1a', textDecoration: isCancelOrRefund ? 'line-through' : 'none' }}>{fmt(salePriceTotal)}</td>
        <td style={{ ...tdStyle, color: '#E24B4A', textDecoration: isCancelOrRefund ? 'line-through' : 'none' }}>{fmt(settlement - salePriceTotal)}</td>
        <td style={{ ...tdStyle, textDecoration: isCancelOrRefund ? 'line-through' : 'none', color: isCancelOrRefund ? '#aaa' : '#1a1a1a' }}>{fmt(settlement)}</td>
        <td style={{ ...tdStyle, textDecoration: isCancelOrRefund ? 'line-through' : 'none', color: isCancelOrRefund ? '#aaa' : '#1a1a1a' }}>{fmt(purchase)}</td>
        <td style={{ ...tdStyle, color: margin >= 0 ? '#1D9E75' : '#E24B4A', fontWeight: '500' }}>{margin >= 0 ? '+' : ''}{fmt(margin)}</td>
        <td style={{ ...tdStyle, color: isCancelOrRefund ? (margin !== 0 ? marginRateColor(margin/salePriceTotal*100) : '#aaa') : (salePriceTotal > 0 ? marginRateColor(margin/salePriceTotal*100) : '#aaa') }}>{isCancelOrRefund ? (margin !== 0 ? (margin/salePriceTotal*100).toFixed(1)+'%' : '-') : (salePriceTotal > 0 ? (margin/salePriceTotal*100).toFixed(1)+'%' : '-')}</td>
      </tr>
    );
  };

  const totalMargin = parseInt(summary.total_margin || 0);
  const totalSales = parseInt(summary.total_sales || 0);

  const renderMonthView = () => {
    const grouped = groupByDay();
    const sortedEntries = Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '960px', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{width:'32px'}}/><col style={{width:'70px'}}/><col style={{width:'60px'}}/>
            <col style={{width:'100px'}}/><col style={{width:'85px'}}/><col style={{width:'100px'}}/>
            <col style={{width:'100px'}}/><col style={{width:'90px'}}/><col style={{width:'90px'}}/>
            <col style={{width:'103px'}}/>
          </colgroup>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>날짜</th>
              <th style={thStyle}>주문수</th>
              <th style={thStyle}>판매금</th>
              <th style={thStyle}>수수료</th>
              <th style={thStyle}>정산예정</th>
              <th style={thStyle}>구입비</th>
              <th style={thStyle}>부가세</th>
              <th style={thStyle}>마진</th>
              <th style={thStyle}>마진율</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const totalVat = Math.round(totalSales * 0.1 - parseInt(summary.total_purchase || 0) * 0.1);
              const totalMarginWithVat = parseInt(summary.total_settlement || 0) - parseInt(summary.total_purchase || 0) - totalVat;
              return (
                <tr style={{ background: '#f5f5f5', fontWeight: '500' }}>
                  <td style={tdStyle}>-</td>
                  <td style={tdStyle}>합계</td>
                  <td style={tdStyle}>{summary.order_count}</td>
                  <td style={tdStyle}>{fmt(summary.total_sales)}</td>
                  <td style={{ ...tdStyle, color: '#E24B4A' }}>{fmt(parseInt(summary.total_settlement||0) - totalSales)}</td>
                  <td style={tdStyle}>{fmt(summary.total_settlement)}</td>
                  <td style={tdStyle}>{fmt(summary.total_purchase)}</td>
                  <td style={{ ...tdStyle, color: '#888' }}>{fmt(totalVat)}</td>
                  <td style={{ ...tdStyle, color: totalMarginWithVat>=0?'#1D9E75':'#E24B4A', fontWeight:'500' }}>{totalMarginWithVat>=0?'+':''}{fmt(totalMarginWithVat)}</td>
                  <td style={{ ...tdStyle, color: totalSales>0?marginRateColor(totalMarginWithVat/totalSales*100):'#aaa' }}>{totalSales>0?(totalMarginWithVat/totalSales*100).toFixed(1)+'%':'-'}</td>
                </tr>
              );
            })()}
            {sortedEntries.length === 0 ? (
              <tr><td colSpan="10" style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>데이터가 없습니다</td></tr>
            ) : (
              sortedEntries.map(([day, dayOrders], idx) => {
                const isCR = o => o.status === 'cancel' || o.status === 'refund_done';
                const daySales = dayOrders.reduce((s, o) => s + (isCR(o) ? 0 : (o.items?.reduce((ss,i)=>ss+(i.sale_price||0),0)||0) + (o.shipping_fee||0)), 0);
                const daySettlement = dayOrders.reduce((s, o) => s + (isCR(o) ? 0 : (o.items?.reduce((ss,i)=>ss+(i.settlement_price||0),0)||0) + (o.shipping_settlement||0)), 0);
                const dayFee = daySales - daySettlement;
                const dayPurchase = dayOrders.reduce((s, o) => s + (isCR(o) ? o.items?.reduce((ss,i)=>{const pr=i.purchase_record||{};return ss+Math.abs(pr.extra_cost||0);},0)||0 : o.items?.reduce((ss,i)=>{const pr=i.purchase_record||{};return ss+(pr.purchase_price||0)+(pr.shipping_cost||0);},0)||0), 0);
                const dayVat = Math.round(daySales * 0.1 - dayPurchase * 0.1);
                const dayMargin = daySettlement - dayPurchase - dayVat;
                const d = new Date(day);
                const dayNames = ['일','월','화','수','목','금','토'];
                const dateLabel = `${d.getMonth()+1}/${d.getDate()} (${dayNames[d.getDay()]})`;
                const isLowMargin = dayMargin < 60000;
                return (
                  <tr key={day}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9f9f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <td style={tdStyle}>{idx + 1}</td>
                    <td style={{ ...tdStyle, background: isLowMargin ? '#FFFBE6' : 'transparent' }}>{dateLabel}</td>
                    <td style={tdStyle}>{dayOrders.length}</td>
                    <td style={tdStyle}>{fmt(daySales)}</td>
                    <td style={{ ...tdStyle, color: '#E24B4A' }}>{fmt(dayFee)}</td>
                    <td style={tdStyle}>{fmt(daySettlement)}</td>
                    <td style={tdStyle}>{fmt(dayPurchase)}</td>
                    <td style={{ ...tdStyle, color: '#888' }}>{fmt(dayVat)}</td>
                    <td style={{ ...tdStyle, color: dayMargin>=0?'#1D9E75':'#E24B4A', fontWeight:'500' }}>{dayMargin>=0?'+':''}{fmt(dayMargin)}</td>
                    <td style={{ ...tdStyle, color: daySales>0?marginRateColor(dayMargin/daySales*100):'#aaa' }}>{daySales>0?(dayMargin/daySales*100).toFixed(1)+'%':'-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderOrderView = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '960px', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{width:'28px'}}/><col style={{width:'128px'}}/><col style={{width:'108px'}}/><col style={{width:'86px'}}/><col style={{width:'185px'}}/>
          <col style={{width:'72px'}}/><col style={{width:'64px'}}/><col style={{width:'72px'}}/><col style={{width:'64px'}}/><col style={{width:'72px'}}/><col style={{width:'61px'}}/>
        </colgroup>
        <thead>
          <tr>
            <th style={thStyle}>#</th>
            <th style={thStyle}>주문일시</th>
            <th style={thStyle}>주문번호</th>
            <th style={thStyle}>판매처</th>
            <th style={{...thStyle, textAlign:'left'}}>상품명</th>
            <th style={thStyle}>판매금{<br/>}(매출)</th>
            <th style={thStyle}>수수료</th>
            <th style={thStyle}>정산예정</th>
            <th style={thStyle}>구입비{<br/>}(매입)</th>
            <th style={thStyle}>마진</th>
            <th style={thStyle}>마진율</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 ? (
            <tr><td colSpan="11" style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>데이터가 없습니다</td></tr>
          ) : (
            orders.map((o, i) => renderOrderRow(o, i+1))
          )}
          {orders.length > 0 && (
            <tr style={{ background: '#f5f5f5', fontWeight: '500' }}>
              <td colSpan="5" style={{ ...tdStyle, textAlign: 'left' }}>일 종합 · {summary.order_count}건</td>
              <td style={tdStyle}>{fmt(summary.total_sales)}</td>
              <td style={tdStyle}>-</td>
              <td style={tdStyle}>{fmt(summary.total_settlement)}</td>
              <td style={tdStyle}>{fmt(summary.total_purchase)}</td>
              <td style={{ ...tdStyle, color: totalMargin>=0?'#1D9E75':'#E24B4A', fontWeight:'500' }}>{totalMargin>=0?'+':''}{fmt(totalMargin)}</td>
              <td style={{ ...tdStyle, color: totalMargin>=0?'#1D9E75':'#E24B4A' }}>{totalSales>0?(totalMargin/totalSales*100).toFixed(1)+'%':'-'}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ minWidth: '1000px', maxWidth: '1000px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h1 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>매출 통계</h1>
      </div>

      {/* 탭 */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[['today','오늘'],['yesterday','어제'],['month','이번달'],['lastmonth','지난달'],['custom','직접입력']].map(([k,v]) => (
          <button key={k} onClick={() => setPeriod(k)} style={{ fontSize: '12px', padding: '4px 14px', borderRadius: '20px', border: `0.5px solid ${period===k?'#1D9E75':'#e8e8e8'}`, background: period===k?'#E1F5EE':'#fff', color: period===k?'#0F6E56':'#888', cursor: 'pointer' }}>{v}</button>
        ))}
        {period === 'custom' && (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button onClick={() => setDatePickerOpen(p => !p)} style={{ fontSize: '12px', padding: '4px 14px', borderRadius: '8px', border: '0.5px solid #1D9E75', background: '#E1F5EE', color: '#0F6E56', cursor: 'pointer' }}>
              {customFrom && customTo ? `${customFrom.getMonth()+1}/${customFrom.getDate()} ~ ${customTo.getMonth()+1}/${customTo.getDate()}` : '날짜 범위 선택'}
            </button>
            {datePickerOpen && (
              <div style={{ position: 'absolute', top: '34px', left: 0, zIndex: 1000, background: '#fff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.15)', padding: '12px' }}>
                <DatePicker
                  selected={customFrom}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setCustomFrom(start);
                    setCustomTo(end);
                    if (start && end) setDatePickerOpen(false);
                  }}
                  startDate={customFrom}
                  endDate={customTo}
                  selectsRange
                  inline
                  locale={ko}
                  dateFormat="yyyy/MM/dd"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 판매처 탭 */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '0.5px solid #e8e8e8', marginBottom: '14px' }}>
        {[['','전체'],['스마트스토어','스마트스토어'],['쿠팡','쿠팡'],['11번가','11번가'],['지마켓','지마켓'],['옥션','옥션'],['셀러허브','셀러허브'],['토스쇼핑','토스쇼핑']].map(([k,v]) => (
          <button key={k} onClick={() => setPlatformFilter(k)} style={{ fontSize: '12px', padding: '7px 14px', cursor: 'pointer', color: platformFilter===k?'#1D9E75':'#888', borderBottom: `2px solid ${platformFilter===k?'#1D9E75':'transparent'}`, background: 'none', border: 'none', fontWeight: platformFilter===k?'500':'400', marginBottom: '-0.5px' }}>{v}</button>
        ))}
      </div>

      {/* 종합 카드 */}
      <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '16px', background: '#fff', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '12px' }}>{summaryTitle()}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '10px' }}>
          {[
            { label: '판매금', sublabel: '(매출)', value: fmt(summary.total_sales) },
            { label: '정산예정금액', sublabel: '', value: fmt(summary.total_settlement) },
            { label: '구입비', sublabel: '(매입)', value: fmt(summary.total_purchase) },
            { label: '마진', sublabel: '', value: (totalMargin>=0?'+':'')+fmt(totalMargin), hl: true, color: totalMargin>=0?'#1D9E75':'#E24B4A' },
          ].map((c,i) => (
            <div key={i} style={{ background: c.hl?'#E1F5EE':'#f5f5f5', borderRadius: '8px', padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: c.hl?'#0F6E56':'#888', marginBottom: '2px' }}>{c.label}</div>
              <div style={{ fontSize: '10px', color: c.hl?'#0F6E56':'#bbb', marginBottom: '4px', minHeight: '14px' }}>{c.sublabel}</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: c.color || '#1a1a1a' }}>{c.value}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: '11px', color: '#aaa', padding: '6px 10px', background: '#f5f5f5', borderRadius: '6px', textAlign: 'center' }}>
          마진은 단순 영업마진(정산금 - 매입)입니다. 실제 순마진과 다를 수 있습니다.<br/>부가세 항목은 아래 세부 리스트에서 확인해주세요.<br/>부가세는 실제보다 더 잡았습니다. (실제로는 적게 나올 가능성이 있습니다)
        </div>
      </div>

      {/* 페이지당 - 이번달/지난달은 숨김 */}
      {!isMonthView && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#888' }}>페이지당</span>
            <select value={pageLimit} onChange={e => { setPageLimit(parseInt(e.target.value)); setCurrentPage(1); }} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8' }}>
              {[20,30,50,100,200].map(n => <option key={n} value={n}>{n}건</option>)}
            </select>
            <span style={{ fontSize: '12px', color: '#aaa' }}>총 {total}건</span>
          </div>
        </div>
      )}

      {/* 테이블 */}
      {isMonthView ? renderMonthView() : renderOrderView()}

      {/* 페이지네이션 - 이번달/지난달은 숨김 */}
      {!isMonthView && totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'4px', marginTop:'16px' }}>
          <button onClick={()=>setCurrentPage(1)} disabled={currentPage===1} style={{ fontSize:'12px', padding:'5px 10px', borderRadius:'8px', border:'0.5px solid #e8e8e8', background:'#fff', cursor:'pointer', color:currentPage===1?'#ccc':'#444' }}>{'<<'}</button>
          <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1} style={{ fontSize:'12px', padding:'5px 10px', borderRadius:'8px', border:'0.5px solid #e8e8e8', background:'#fff', cursor:'pointer', color:currentPage===1?'#ccc':'#444' }}>{'<'}</button>
          {Array.from({length:totalPages},(_,i)=>i+1).filter(p=>p===1||p===totalPages||Math.abs(p-currentPage)<=2).map((p,i,arr)=>[
            i>0&&arr[i-1]!==p-1&&<span key={'e'+p} style={{fontSize:'12px',color:'#aaa',padding:'0 4px'}}>...</span>,
            <button key={p} onClick={()=>setCurrentPage(p)} style={{ fontSize:'12px', padding:'5px 10px', borderRadius:'8px', border:'0.5px solid #e8e8e8', background:currentPage===p?'#1D9E75':'#fff', color:currentPage===p?'#fff':'#1a1a1a', cursor:'pointer', minWidth:'34px' }}>{p}</button>
          ])}
          <button onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages} style={{ fontSize:'12px', padding:'5px 10px', borderRadius:'8px', border:'0.5px solid #e8e8e8', background:'#fff', cursor:'pointer', color:currentPage===totalPages?'#ccc':'#444' }}>{'>'}</button>
          <button onClick={()=>setCurrentPage(totalPages)} disabled={currentPage===totalPages} style={{ fontSize:'12px', padding:'5px 10px', borderRadius:'8px', border:'0.5px solid #e8e8e8', background:'#fff', cursor:'pointer', color:currentPage===totalPages?'#ccc':'#444' }}>{'>>'}</button>
        </div>
      )}
    </div>
  );
}
